import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Plus, Trash2, Archive } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtDate, BACKEND } from "@/lib/api";
import { PageHeader, CategoryBadge } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { AdminProviders } from "@/pages/AdminProviders";
import { AuditTab, ImportTab, EmailTab } from "@/pages/AdminExtra";

const UsersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success("Gespeichert"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="glass overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Nutzer", "Rolle", "Heute", "Tageslimit", "Gesperrt", "Seit"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-row-${u.email}`}>
              <td className="px-4 py-3"><div className="text-white">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
              <td className="px-4 py-3">
                <select disabled={u.id === me.id} data-testid={`admin-role-${u.email}`} className="field py-1.5" value={u.role} onChange={(e) => patch(u.id, { role: e.target.value })}>
                  <option value="user">Nutzer</option><option value="admin">Admin</option>
                </select>
              </td>
              <td className="px-4 py-3 text-slate-300">{u.today}</td>
              <td className="px-4 py-3">
                {u.role === "admin" ? <span className="text-xs text-slate-500">kein Limit</span> :
                  <input type="number" min={0} defaultValue={u.daily_limit} data-testid={`admin-limit-${u.email}`} className="field w-24 py-1.5"
                    onBlur={(e) => Number(e.target.value) !== u.daily_limit && patch(u.id, { daily_limit: Number(e.target.value) })} />}
              </td>
              <td className="px-4 py-3"><Switch disabled={u.id === me.id} checked={u.disabled} onCheckedChange={(v) => patch(u.id, { disabled: v })} data-testid={`admin-disable-${u.email}`} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EndpointsTab = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ base_url: "", label: "" });
  const load = () => api.get("/admin/trusted-endpoints").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/trusted-endpoints", f); setF({ base_url: "", label: "" }); toast.success("Endpunkt freigegeben"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="glass p-6">
      <p className="mb-4 text-sm text-slate-400">Private und Loopback-Adressen (z. B. http://localhost:11434) sind für Nutzer gesperrt, bis sie hier freigegeben werden. Nur vertrauenswürdige Inferenz-Server (Ollama/vLLM) eintragen.</p>
      <form onSubmit={add} className="flex flex-col gap-3 md:flex-row">
        <input required data-testid="endpoint-url-input" className="field" placeholder="http://ollama:11434" value={f.base_url} onChange={(e) => setF({ ...f, base_url: e.target.value })} />
        <input data-testid="endpoint-label-input" className="field md:w-56" placeholder="Bezeichnung" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <button data-testid="endpoint-add-button" className="btn-grad inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-2"><Plus className="h-4 w-4" />Freigeben</button>
      </form>
      <div className="mt-5 space-y-2" data-testid="endpoint-list">
        {list.length === 0 && <div className="text-sm text-slate-500">Noch keine Endpunkte freigegeben.</div>}
        {list.map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-lg border border-white/5 px-4 py-3">
            <div><div className="font-mono text-sm text-white">{e.base_url}</div><div className="text-xs text-slate-500">{e.label} · {e.created_by}</div></div>
            <button onClick={async () => { await api.delete(`/admin/trusted-endpoints/${e.id}`); load(); }} data-testid={`endpoint-delete-${e.id}`} className="btn-ghost p-2 text-red-300"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExportsTab = () => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/exports").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy(true);
    try { await api.post("/admin/exports", { label: "manual" }); toast.success("Export erstellt"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="glass p-6">
      <p className="mb-4 text-sm text-slate-400">Projekt-ZIP ohne .env, Keys, node_modules und Caches – mit Manifest (Dateiliste, Testergebnisse, offene Punkte).</p>
      <button onClick={create} disabled={busy} data-testid="export-create-button" className="btn-grad inline-flex items-center gap-2 px-5 py-2.5"><Archive className="h-4 w-4" />{busy ? "Erstelle ZIP…" : "Neuen Export erstellen"}</button>
      <div className="mt-5 space-y-2" data-testid="export-list">
        {list.map((x) => (
          <div key={x.name} className="flex items-center justify-between rounded-lg border border-white/5 px-4 py-3">
            <div className="font-mono text-xs text-slate-300 break-all">{x.name} <span className="text-slate-500">· {(x.size / 1048576).toFixed(1)} MB</span></div>
            <a href={`${BACKEND}${x.url}`} data-testid={`export-download-${x.name}`} className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs text-white"><Download className="h-3.5 w-3.5" />Download (nur Admin)</a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Admin() {
  return (
    <div data-testid="admin-page">
      <PageHeader title="Admin" subtitle="Nutzer, Rollen, Tageslimits, Provider-Freigaben, lokale Endpunkte und Projekt-Export." />
      <Tabs defaultValue="users">
        <TabsList className="mb-6 bg-white/5">
          <TabsTrigger value="users" data-testid="admin-tab-users">Nutzer</TabsTrigger>
          <TabsTrigger value="providers" data-testid="admin-tab-providers">Provider</TabsTrigger>
          <TabsTrigger value="endpoints" data-testid="admin-tab-endpoints">Lokale Endpunkte</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import">Katalog-Import</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email">E-Mail</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit-Log</TabsTrigger>
          <TabsTrigger value="exports" data-testid="admin-tab-exports">Export</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="providers"><AdminProviders /></TabsContent>
        <TabsContent value="endpoints"><EndpointsTab /></TabsContent>
        <TabsContent value="import"><ImportTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="exports"><ExportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
